import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { HeroVideoPager } from "@/components/HeroVideoPager";
import { useVideoPdfAttachments } from "@/hooks/useVideoPdfAttachments";
import titleIcon from "@/assets/recently_iamerica.webp";

const HowItWorks = () => {
  const { t, i18n } = useTranslation("landing");
  const navigate = useNavigate();
  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  const { hasPdfSet } = useVideoPdfAttachments();

  const { data: videos, isLoading } = useQuery({
    queryKey: ["landing-recent-grid", lang],
    queryFn: async () => {
      const baseSelect = "id, title, title_es, description, description_es, thumbnail_url, duration_minutes, video_price_tiers(price, name, billing_interval, access_type), video_library_categories(video_categories(id, name, name_es, color, display_order))";

      const { data: featured, error: featuredErr } = await supabase
        .from("video_library")
        .select(baseSelect)
        .eq("is_published", true)
        .eq("show_on_homepage", true)
        .order("created_at", { ascending: false })
        .order("display_order", { ascending: true })
        .limit(8);
      if (featuredErr) throw featuredErr;

      let data = featured;
      if (!data || data.length === 0) {
        const { data: fallback, error: fallbackErr } = await supabase
          .from("video_library")
          .select(baseSelect)
          .eq("is_published", true)
          .order("created_at", { ascending: false })
          .limit(8);
        if (fallbackErr) throw fallbackErr;
        data = fallback;
      }

      const { getCategoryColor } = await import("@/lib/categoryColors");

      return (data || []).map((v: any) => {
        const tier = v.video_price_tiers || {};
        const price = tier.price ?? 0;
        const accessType: string = tier.access_type || (price === 0 ? "free" : "one_time");
        const firstCat = (v.video_library_categories || [])
          .map((l: any) => l.video_categories)
          .filter(Boolean)
          .sort((a: any, b: any) => (a.display_order ?? 999) - (b.display_order ?? 999))[0];

        return {
          id: v.id,
          title: lang === "es" && v.title_es ? v.title_es : v.title,
          thumbnail_url: v.thumbnail_url || null,
          duration_minutes: v.duration_minutes,
          price,
          is_free: accessType === "free",
          is_all_access: accessType === "subscription",
          category_name: firstCat ? (lang === "es" && firstCat.name_es ? firstCat.name_es : firstCat.name) : null,
          category_color: firstCat ? getCategoryColor(firstCat) : null,
        };
      });

    },
  });

  return (
    <section id="how-it-works" className="w-full scroll-mt-24 bg-white pt-2.5 pb-2.5 md:scroll-mt-28 md:pt-[14px] md:pb-2">
      <div className="container mx-auto px-4 lg:px-6">
        <div className="mx-auto max-w-6xl mb-4">
          <div className="flex items-center gap-2 md:gap-3">
            <img
              src={titleIcon}
              alt=""
              aria-hidden="true"
              className="w-8 h-8 md:h-[55px] md:w-auto md:max-h-[55px] object-contain"
            />
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-foreground">
                {t("hero.grid_label")}
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground">
                {t("hero.grid_sublabel", "The newest lessons in our library.")}
              </p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-video rounded-xl bg-[#070e1e]/10 animate-pulse" />
            ))}
          </div>
        ) : videos && videos.length > 0 ? (
          <div className="mx-auto max-w-6xl">
            <HeroVideoPager
              videos={videos}
              hasPdfSet={hasPdfSet}
              variant="light"
              mobileMode="scroll"
              desktopMode="carousel"
              onSelect={(id) => navigate(`/videos?course=${id}`)}
            />
          </div>
        ) : (
          <div className="py-12 text-center text-[#070e1e]/60">
            {t("hero.grid_empty")}
          </div>
        )}

        <div className="mt-3 flex justify-center md:mt-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/videos")}
            className="gap-2 text-[#070e1e] hover:bg-[#070e1e]/5 hover:text-[#fcb424]"
          >
            {t("hero.grid_view_all")}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
